import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {IStation} from '../../../models/Station'
import {defaultMapZoomLevel, maxZoomLevel} from '../../config/constants'
import {useStations} from '../../helpers/cachedRequest'
import {isCamp} from '../../helpers/station'
import {usePrev} from '../hocs/hooks'
import SingleStation from '../user/SingleStation'
import MapsInfoWindow from './MapsInfoWindow'
import {IMaps} from './mapsUtil'
import StationMapsOverlay from './StaionMapsOverlay'

const MapsStations = (
  { maps, filter, onChange, initialSelected, initStations, firstSelection, zoom }: {
    maps: IMaps
    onChange?: (station: IStation) => any
    filter?: (station: IStation) => boolean
    initialSelected?: IStation,
    firstSelection: boolean,
    initStations?:　ReadonlyArray<IStation>,
    zoom?: number
  }
) => {
  const stations = useStations()
  const [selected, setSelected] = useState(initialSelected)
  const openView = useCallback((station: IStation) => {
    setSelected(station)
    onChange?.(station)
  }, [onChange])
  const filteredStations = useMemo(() => (initStations || stations)
    .filter(station => !filter || filter(station)),
  [initStations, stations, filter])
  const openViews = useMemo(() => Object.fromEntries(
    (initStations || stations).map(station => [station._id, () => openView(station)])
  ), [initStations, stations, openView])
  const prevInitialSelected = usePrev(initialSelected)
  useEffect(() => void (
    initialSelected !== prevInitialSelected && initialSelected !== selected && setSelected(initialSelected)
  ), [selected, initialSelected, prevInitialSelected])
  const closeCurrent = useCallback(() => {
    if (selected) {
      setSelected(undefined)
      onChange?.(undefined)
    }
  }, [selected, onChange])

  const addressCounts: Record<string, any> = {}
  const stationsWithAddress = useMemo(() => zoom && zoom >= defaultMapZoomLevel
    ? filteredStations.map(station => {
      const { prefecture, region, address } = station
      const fullAddress = `${prefecture}${region}${address}`
      addressCounts[fullAddress] = addressCounts[fullAddress] === undefined ? 0 : addressCounts[fullAddress] + 1
      return { ...station, ...addressCounts[fullAddress] && { sameAddressIndex: addressCounts[fullAddress] } }
    })
    : filteredStations, [zoom])
  return <>
    {
      selected
      && <MapsInfoWindow
        lat={selected.location[1]}
        lng={selected.location[0]}
        maps={maps}
        onClose={closeCurrent}
      >
        <SingleStation key={selected._id} station={selected} isInfoWindow firstSelection={firstSelection}/>
      </MapsInfoWindow>
    }
    {(stationsWithAddress || [])
      .map(
        station => {
          const {
            location: [lng, lat],
            activityOnly,
            _id
          } = station
          return <StationMapsOverlay
            key={_id}
            maps={maps}
            price={activityOnly ? station.activityPrice : station.price}
            isCamp={isCamp(station)}
            activityOnly={activityOnly}
            lat={zoom && zoom >= defaultMapZoomLevel && (station.sameAddressIndex || 0)
              ? lat - 0.00002 * ((maxZoomLevel + 1 - Math.min(zoom, maxZoomLevel)) ** 2) * (station.sameAddressIndex || 0)
              : lat
            }
            lng={lng}
            onClick={openViews[_id]}
            zoom={zoom}
          />
        }
      )}
  </>
}
MapsStations.displayName = 'MapsStations'
export default MapsStations
